import React, { FC, ReactNode, useState } from "react";
import { useDispatch, useSelector }       from "react-redux";
import { Button, Form, Icon, Modal } from "semantic-ui-react";
import GetPlanReservationId          from "../../../application/usecase/plan/GetPlanReservationId";
import DeleteReservation             from "../../../application/usecase/reservation/DeleteReservation";
import RegisterReservation           from "../../../application/usecase/reservation/RegisterReservation";
import RegisterReservationPlan       from "../../../application/usecase/reservation/RegisterReservationPlan";
import CustomerRepository            from "../../../domain/customer/CustomerRepository";
import Facility                      from "../../../domain/facility/Facility";
import Time                          from "../../../domain/fundamentals/date/Time";
import TimePeriod                    from "../../../domain/fundamentals/date/TimePeriod";
import Plan                          from "../../../domain/plan/Plan";
import Reservation                   from "../../../domain/reservation/Reservation";
import ReservationRepository         from "../../../domain/reservation/ReservationRepository";
import Reservations                  from "../../../domain/reservation/Reservations";
import { ResKaState }                from "../../../index";
import CustomerDataSource            from "../../../infrastructure/datasource/customer/CustomerDataSource";
import ReservationDataSource         from "../../../infrastructure/datasource/reservation/ReservationDataSource";
import { useSelectedPlan }           from "../../../infrastructure/redux/plan/selected";
import RouterDispatcher              from "../../../infrastructure/redux/router/RouterDispatcher";
import TopSection                    from "../common/layout/TopSection";

interface Props {
  reservations: Reservations;
  facility: Facility;
  from: Time;
  to: Time;
}

const ReservationButton: FC<Props> = (props: Props) => {
  const {reservations, facility, from, to} = props;
  const [open, setOpen] = useState(false);
  const [reservationPlan, setReservationPlan] = useState(new Plan());
  const reservation: Reservation = useSelector((state: ResKaState) => state.reservation);
  const selectedPlan: Plan = useSelectedPlan();
  const repository: ReservationRepository = new ReservationDataSource(useDispatch());
  const customerRepository: CustomerRepository = new CustomerDataSource(useDispatch());
  const registerReservation: RegisterReservation = new RegisterReservation(useDispatch());
  const registerReservationPlan: RegisterReservationPlan = new RegisterReservationPlan(useDispatch());
  const getPlanReservationId: GetPlanReservationId = new GetPlanReservationId();
  const deleteReservation: DeleteReservation = new DeleteReservation(useDispatch());
  const routerDispatcher: RouterDispatcher = new RouterDispatcher(useDispatch());

  const [emptyOpen, setEmptyOpen] = useState(false);

  const emptyReservationPlanTime = (reservation: Reservation, selectedPlan: Plan): ReactNode => {
    const from: Time = reservation.detail.timePeriod.from.clone();
    return (
      <p>{ `${ reservation.detail.date.asText() } ${ reservation.detail.timePeriod.from.asText() } から ${ from.addPlanTimeType(selectedPlan.timeType).asText() }` }</p>
    );
  }
  const empty = (
    <>
      <Button
        icon
        onClick={ () => {
          repository.setReservationFacility(facility);
          repository.setReservationTimePeriod(new TimePeriod(from, to));
          setEmptyOpen(true);
        } }
      >
        <Icon name="circle outline"/>
      </Button>

      <Modal
        open={ emptyOpen }
      >
        <Modal.Header>予約内容の確認</Modal.Header>

        <Modal.Content>
          <TopSection>
            {
              reservation.customer.isEmpty()
                ?
                <p>予約する顧客が選択されていません。</p>
                :
                <p>以下の内容で予約を確定します。よろしいですか？</p>
            }
          </TopSection>
          <Form>
            <Form.Field error={ reservation.customer.isEmpty() }>
              <label>顧客氏名</label>
              {
                reservation.customer.isEmpty()
                  ?
                  <p>未選択</p>
                  :
                  <p>{ `${ reservation.customer.detail.name.lastName.value } ${ reservation.customer.detail.name.firstName.value }` }</p>
              }
            </Form.Field>
            <Form.Field>
              <label>プラン</label>
              {
                selectedPlan.id.isEmpty() ?
                  <>
                    <p>プランの選択なし</p>
                  </>
                  :
                  <>
                    <p>{ selectedPlan.name.toString() }</p>
                  </>
              }
            </Form.Field>
            <Form.Field>
              <label>予約施設・設備</label>
              <p>{ reservation.facility.name.value }</p>
            </Form.Field>
            <Form.Field>
              <label>対応スタッフ</label>
              {
                reservation.staff.isEmpty()
                  ?
                  <>
                    <p>未選択</p>
                    <p>スタッフフリーとして扱われます。</p>
                  </>
                  :
                  <p>{ `${ reservation.staff.name.lastName.value } ${ reservation.staff.name.firstName.value }` }</p>
              }
            </Form.Field>
            <Form.Field>
              <label>予約日時</label>
              {
                selectedPlan.id.isEmpty()
                  ?
                  <p>{ `${ reservation.detail.date.asText() } ${ reservation.detail.timePeriod.from.asText() } から ${ reservation.detail.timePeriod.to.asText() }` }</p>
                  :
                  emptyReservationPlanTime(reservation, selectedPlan)
              }
            </Form.Field>
          </Form>
        </Modal.Content>

        <Modal.Actions>
          <Button
            icon
            labelPosition="left"
            disabled={ reservation.customer.isEmpty() || reservation.facility.isEmpty() }
            positive={ !reservation.customer.isEmpty() && !reservation.facility.isEmpty() }
            onClick={ () => {
              if (selectedPlan.id.isEmpty()) {
                registerReservation.execute(reservation);
              } else {
                registerReservationPlan.execute(reservation, selectedPlan);
              }
              setEmptyOpen(false);
            } }
          >
            <Icon name="check"/>
            予約する
          </Button>

          <Button
            onClick={ () => setEmptyOpen(false) }
          >
            閉じる
          </Button>
        </Modal.Actions>
      </Modal>
    </>
  );

  if (reservations.isEmpty()) {
    return empty;
  }

  return (
    <>
      {
        reservations.toList().map(reservation => (
          <>
            <Button
              icon
              labelPosition="left"
              onClick={ () => {
                setOpen(true);
                getPlanReservationId.execute(reservation.id).then(plan => setReservationPlan(plan)).catch(() => setReservationPlan(new Plan()));
              } }
            >
              <Icon name="clock"/>
              { `${ reservation.customer.detail.name.lastName.value } ${ reservation.customer.detail.name.firstName.value }` }
            </Button>

            <Modal
              open={ open }
            >
              <Modal.Header>予約情報の確認</Modal.Header>

              <Modal.Content>
                <Form>
                  <Form.Field>
                    <label>顧客氏名</label>
                    <Button
                      icon
                      labelPosition="left"
                      onClick={ () => {
                        customerRepository.setCustomer(reservation.customer);
                        routerDispatcher.customerEdit();
                      } }
                    >
                      <Icon name="edit"/>
                      { `${ reservation.customer.detail.name.lastName.value } ${ reservation.customer.detail.name.firstName.value }` }
                    </Button>
                  </Form.Field>
                  <Form.Field>
                    <label>プラン</label>
                    {
                      reservationPlan.id.isEmpty() ?
                        <>
                          <p>プランなし</p>
                        </>
                        :
                        <>
                          <p>{ reservationPlan.name.toString() }</p>
                        </>
                    }
                  </Form.Field>
                  <Form.Field>
                    <label>予約施設・設備</label>
                    <p>{ reservation.facility.name.value }</p>
                  </Form.Field>
                  <Form.Field>
                    <label>対応スタッフ</label>
                    {
                      reservation.staff.isEmpty()
                        ?
                        <>
                          <p>スタッフフリー</p>
                        </>
                        :
                        <p>{ `${ reservation.staff.name.lastName.value } ${ reservation.staff.name.firstName.value }` }</p>
                    }
                  </Form.Field>
                  <Form.Field>
                    <label>予約日時</label>
                    <p>{ `${ reservation.detail.date.asText() } ${ reservation.detail.timePeriod.from.asText() } から ${ reservation.detail.timePeriod.to.asText() }` }</p>
                  </Form.Field>
                </Form>
              </Modal.Content>

              <Modal.Actions>
                <Button
                  icon
                  labelPosition="left"
                  negative
                  onClick={ () => {
                    deleteReservation.execute(reservation);
                    setOpen(false);
                  } }
                >
                  <Icon name="user cancel"/>
                  予約をキャンセルする
                </Button>

                <Button
                  onClick={ () => setOpen(false) }
                >
                  閉じる
                </Button>
              </Modal.Actions>
            </Modal>
          </>
        ))
      }
    </>
  );
};

export default ReservationButton;
